import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { UTILITIES } from "@/constants/utilities";
import { BRAESIDE_LOGO_URL } from "@/constants/brand";
import {
  MapPinned, PencilRuler, Sparkles, ShieldCheck, ArrowRight, Check,
  Globe2, FileText, Pin as PinIcon, Ruler, Mail,
} from "lucide-react";
import GetTheApp from "@/components/GetTheApp";

const Section = ({ children, className = "" }) => (
  <section className={`max-w-[1240px] mx-auto px-6 md:px-10 ${className}`}>{children}</section>
);

const StepCard = ({ n, title, body, Icon }) => (
  <div className="border-2 border-foreground bg-background p-6 md:p-7 relative transition-transform hover:-translate-y-1 hover:shadow-block">
    <div className="absolute -top-3 -left-3 bg-foreground text-background w-9 h-9 grid place-items-center font-heading font-black text-lg border-2 border-foreground">
      {n}
    </div>
    <Icon className="w-8 h-8 mb-4" strokeWidth={2.25} />
    <div className="font-heading uppercase font-black text-xl">{title}</div>
    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{body}</p>
  </div>
);

const PricePreview = ({ name, tagline, price, cadence, features, cta, popular }) => (
  <div className={`relative border-2 border-foreground bg-background p-7 flex flex-col ${popular ? "shadow-block" : "shadow-block-sm"}`}>
    {popular && (
      <div className="absolute -top-3 left-5 bg-foreground text-background font-heading font-black uppercase text-[10px] tracking-widest px-2 py-1 border-2 border-foreground inline-flex items-center gap-1">
        <Sparkles className="w-3 h-3" /> Most popular
      </div>
    )}
    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{tagline}</div>
    <div className="font-heading font-black uppercase text-3xl mt-1">{name}</div>
    <div className="mt-4">
      <span className="font-heading font-black text-4xl">{price}</span>
      <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground ml-2">{cadence}</span>
    </div>
    <ul className="mt-5 space-y-1.5 flex-1">
      {features.map((f, i) => (
        <li key={i} className="flex items-start gap-2 text-sm">
          <Check className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={3} /><span>{f}</span>
        </li>
      ))}
    </ul>
    <div className="mt-6 font-heading font-bold uppercase text-xs tracking-widest text-muted-foreground">
      {cta}
    </div>
  </div>
);

const Landing = () => {
  const { user } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b-2 border-foreground bg-background/95 backdrop-blur-sm">
        <Section className="flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3 md:gap-4" data-testid="brand-link">
            <img src={BRAESIDE_LOGO_URL} alt="Braeside Plumbing and Drainage" className="h-10 md:h-12 w-auto" />
            <div className="hidden sm:block h-8 w-px bg-foreground/30" />
            <div className="leading-tight">
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground hidden sm:block">Onsite tool</div>
              <span className="font-heading font-black text-lg md:text-2xl uppercase tracking-tight">Field Plan</span>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-xs font-heading font-bold uppercase tracking-widest">
            <a href="#how" className="hover:underline underline-offset-4" data-testid="nav-how">How it works</a>
            <a href="#services" className="hover:underline underline-offset-4" data-testid="nav-services">Services</a>
            <a href="#download" className="hover:underline underline-offset-4" data-testid="nav-download">Download</a>
            <a href="#locator" className="hover:underline underline-offset-4" data-testid="nav-locator">Need a locator?</a>
            <a href="#support" className="hover:underline underline-offset-4" data-testid="nav-support">Support</a>
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <button data-testid="nav-open-dashboard" className="btn-rugged text-xs md:text-sm" onClick={() => nav("/jobs")}>
                Open Jobs
              </button>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className="btn-ghost-rugged text-xs md:text-sm hidden sm:inline-flex">Log in</Link>
                <Link to="/register" data-testid="nav-register" className="btn-rugged text-xs md:text-sm">Get Started</Link>
              </>
            )}
          </div>
        </Section>
      </header>

      {/* Hero */}
      <Section className="grid md:grid-cols-12 gap-10 items-center pt-14 md:pt-20 pb-16 md:pb-24">
        <div className="md:col-span-7">
          <div className="inline-flex items-center gap-2 border-2 border-foreground bg-[#2563EB] text-white px-3 py-1 font-mono text-[11px] uppercase tracking-[0.2em] mb-6">
            <span className="w-2 h-2 bg-white inline-block" /> 14-day free trial · Then AUD $49/year</div>
          <h1 className="font-heading font-black uppercase tracking-tighter text-5xl sm:text-6xl md:text-7xl leading-[0.9]">
            Locate.<br />
            Mark.<br />
            <span className="bg-[#2563EB] text-white px-2 inline-block">Plan onsite.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg text-muted-foreground leading-relaxed">
            Field Plan is the rugged mapping &amp; sketch tool from Braeside Plumbing &amp; Drainage.
            Drop pins on satellite, sketch over site photos and PDF plans, import Google Earth
            files, and email an AI-written site report to your client — all before you've packed the ute.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={user ? "/jobs" : "/register"} data-testid="hero-cta-primary" className="btn-rugged text-base inline-flex items-center gap-2">
              {user ? "Open Jobs" : "Start Locating"} <ArrowRight className="w-4 h-4" strokeWidth={3} />
            </Link>
            <Link to="/login" data-testid="hero-cta-secondary" className="btn-ghost-rugged text-base">
              I have an account
            </Link>
          </div>

          {/* Trust markers */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-xs font-mono uppercase tracking-widest text-muted-foreground">
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-foreground" /> Login-secured jobs</div>
            <div className="flex items-center gap-2"><Globe2 className="w-4 h-4 text-foreground" /> KML / KMZ import</div>
            <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-foreground" /> PDF plans supported</div>
            <div className="flex items-center gap-2"><Ruler className="w-4 h-4 text-foreground" /> Depth annotations (mm)</div>
            <div className="flex items-center gap-2"><PinIcon className="w-4 h-4 text-foreground" /> 16 tradie tools</div>
            <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-foreground" /> AI site reports</div>
          </div>
        </div>

        <div className="md:col-span-5">
          <div className="relative border-2 border-foreground bg-background shadow-block overflow-hidden">
            {/* Detailed plumbing site plan — every visible service colour matches the
                utility palette used inside the app. This is a hand-built SVG so it
                stays crisp at any size and doesn't rely on stock photos. */}
            <svg viewBox="0 0 620 460" role="img" aria-label="Detailed plumbing site plan with colour-coded services, fixtures, depths and dimensions" className="w-full h-[440px] bg-white block">
              <defs>
                <pattern id="fpGrid" width="20" height="20" patternUnits="userSpaceOnUse">
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#2563EB" strokeWidth="0.4" opacity="0.16" />
                </pattern>
                <pattern id="fpConcrete" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                  <line x1="0" y1="0" x2="0" y2="8" stroke="#050505" strokeWidth="0.5" opacity="0.35" />
                </pattern>
                <pattern id="fpGrass" width="14" height="14" patternUnits="userSpaceOnUse">
                  <path d="M2 12 l2 -4 M6 12 l2 -4 M10 12 l2 -4" stroke="#22C55E" strokeWidth="0.7" opacity="0.55" />
                </pattern>
                <marker id="fpArrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                  <path d="M0,0 L10,5 L0,10 z" fill="#050505" />
                </marker>
                <marker id="fpFlow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="5" markerHeight="5" orient="auto">
                  <path d="M0,0 L10,5 L0,10 z" fill="#050505" />
                </marker>
              </defs>

              {/* Blueprint grid backdrop */}
              <rect width="620" height="460" fill="url(#fpGrid)" />

              {/* Site title strip */}
              <g fontFamily="'Barlow Condensed', sans-serif">
                <rect x="14" y="14" width="240" height="34" fill="#050505" />
                <text x="24" y="36" fill="#FAFAFA" fontSize="16" fontWeight="900" letterSpacing="2">SITE PLAN · 42 GEORGE ST</text>
                <rect x="14" y="52" width="160" height="18" fill="#FFFFFF" stroke="#050505" strokeWidth="1.2" />
                <text x="20" y="65" fontSize="9" fontWeight="700" letterSpacing="1.5" fill="#050505">SCALE 1:100 · SHEET 1 OF 1</text>
                <rect x="178" y="52" width="76" height="18" fill="#FFFFFF" stroke="#050505" strokeWidth="1.2" />
                <text x="184" y="65" fontSize="9" fontWeight="700" letterSpacing="1.5" fill="#050505">REV B · JUL 26</text>
              </g>

              {/* North arrow */}
              <g transform="translate(560, 24)" fontFamily="'Barlow Condensed', sans-serif">
                <circle cx="0" cy="20" r="20" fill="#FFFFFF" stroke="#050505" strokeWidth="1.5" />
                <path d="M0 4 L6 24 L0 20 L-6 24 Z" fill="#050505" />
                <text x="-4" y="42" fontSize="10" fontWeight="900">N</text>
              </g>

              {/* Property boundary + boundary bearings */}
              <rect x="60" y="90" width="500" height="300" fill="#FAFAFA" stroke="#050505" strokeWidth="2.5" />
              <g fontFamily="'JetBrains Mono', monospace" fontSize="7" fill="#050505" opacity="0.75">
                <text x="290" y="86">27.45 M — 90°00'</text>
                <text x="290" y="405" textAnchor="middle">27.45 M — 270°00'</text>
                <text x="46" y="245" transform="rotate(-90 46 245)">16.50 M</text>
                <text x="576" y="245" transform="rotate(-90 576 245)">16.50 M</text>
              </g>

              {/* Grass yard */}
              <rect x="60" y="90" width="500" height="300" fill="url(#fpGrass)" opacity="0.5" />
              {/* Driveway (concrete hatch) */}
              <path d="M60 240 L150 240 L150 310 L60 310 Z" fill="url(#fpConcrete)" />
              <text x="76" y="278" fontFamily="'JetBrains Mono', monospace" fontSize="9" letterSpacing="1.4" fill="#050505" opacity="0.7">DRIVEWAY · 100 CONC</text>

              {/* Front path */}
              <path d="M150 200 L230 200 L230 220 L150 220 Z" fill="url(#fpConcrete)" opacity="0.85" />

              {/* Building footprint */}
              <rect x="150" y="140" width="320" height="170" fill="#FFFFFF" stroke="#050505" strokeWidth="2" />
              {/* Internal wall divisions */}
              <line x1="260" y1="140" x2="260" y2="240" stroke="#050505" strokeWidth="1" />
              <line x1="150" y1="240" x2="470" y2="240" stroke="#050505" strokeWidth="1" />
              <line x1="360" y1="140" x2="360" y2="240" stroke="#050505" strokeWidth="1" />
              <line x1="360" y1="240" x2="360" y2="310" stroke="#050505" strokeWidth="1" />

              {/* Room labels */}
              <g fontFamily="'Barlow Condensed', sans-serif" fontSize="9" fontWeight="700" letterSpacing="1.5" fill="#050505" opacity="0.55">
                <text x="200" y="155">BATH</text>
                <text x="290" y="155">KITCHEN</text>
                <text x="395" y="155">LAUNDRY</text>
                <text x="200" y="255">BED 1</text>
                <text x="290" y="255">LIVING</text>
                <text x="395" y="255">BED 2</text>
                <text x="395" y="322">GARAGE</text>
              </g>

              {/* Building label */}
              <rect x="330" y="118" width="140" height="18" fill="#050505" />
              <text x="336" y="131" fontFamily="'Barlow Condensed', sans-serif" fontSize="10" fontWeight="800" letterSpacing="1.6" fill="#FFFFFF">EXIST. DWELLING · S/S BV</text>

              {/* -------- Services -------- */}
              {/* Sewer main (green) - IO to main */}
              <path d="M230 240 L230 370 L560 370" fill="none" stroke="#22C55E" strokeWidth="5" strokeLinecap="round" markerEnd="url(#fpFlow)" />
              {/* Sewer branches inside dwelling */}
              <path d="M200 165 L230 165 L230 240" fill="none" stroke="#22C55E" strokeWidth="3" strokeLinecap="round" />
              <path d="M300 175 L300 240" fill="none" stroke="#22C55E" strokeWidth="3" strokeLinecap="round" />
              <path d="M400 175 L400 240 L360 240" fill="none" stroke="#22C55E" strokeWidth="3" strokeLinecap="round" />

              {/* Water main (blue) — meter to fixtures */}
              <path d="M60 170 L120 170 L120 220 L300 220 L300 165" fill="none" stroke="#2563EB" strokeWidth="5" strokeLinecap="round" />
              <path d="M300 220 L200 220 L200 175" fill="none" stroke="#2563EB" strokeWidth="3" strokeLinecap="round" />
              <path d="M300 220 L400 220 L400 175" fill="none" stroke="#2563EB" strokeWidth="3" strokeLinecap="round" />
              {/* Hot water (red-dashed) from HWU to fixtures */}
              <path d="M420 175 L420 220 L200 220 L200 175" fill="none" stroke="#DC2626" strokeWidth="2" strokeDasharray="4 3" opacity="0.9" />

              {/* Gas (yellow) */}
              <path d="M60 320 L100 320 L100 260 L145 260" fill="none" stroke="#EAB308" strokeWidth="5" strokeLinecap="round" />
              <path d="M155 260 L280 260" fill="none" stroke="#EAB308" strokeWidth="3" strokeLinecap="round" />

              {/* Electrical (amber) — overhead + underground */}
              <path d="M470 90 L470 140" fill="none" stroke="#F59E0B" strokeWidth="5" strokeLinecap="round" strokeDasharray="10 4" />
              <path d="M470 155 L440 155 L440 240" fill="none" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round" />

              {/* NBN (purple) */}
              <path d="M560 130 L510 130 L510 200 L470 200" fill="none" stroke="#9333EA" strokeWidth="4" strokeLinecap="round" />

              {/* Telstra (orange) */}
              <path d="M60 130 L110 130 L110 160" fill="none" stroke="#F97316" strokeWidth="4" strokeLinecap="round" strokeDasharray="4 4" />

              {/* Fire service (red) — western boundary loop */}
              <path d="M60 195 L90 195 L90 330 L60 330" fill="none" stroke="#DC2626" strokeWidth="4" strokeLinecap="round" />

              {/* Irrigation (teal) — rear yard */}
              <path d="M560 320 L500 320 L500 360 L470 360" fill="none" stroke="#14B8A6" strokeWidth="3" strokeDasharray="2 4" />

              {/* Drainage / stormwater (slate) */}
              <path d="M260 310 L260 385 L60 385" fill="none" stroke="#64748B" strokeWidth="4" strokeLinecap="round" />
              {/* Downpipes */}
              <circle cx="150" cy="140" r="3" fill="#64748B" stroke="#050505" strokeWidth="1" />
              <circle cx="470" cy="140" r="3" fill="#64748B" stroke="#050505" strokeWidth="1" />
              <circle cx="150" cy="310" r="3" fill="#64748B" stroke="#050505" strokeWidth="1" />

              {/* Fixtures — with proper trade symbols */}
              <g fontFamily="'JetBrains Mono', monospace" fontSize="7" fill="#050505">
                {/* WC */}
                <ellipse cx="195" cy="160" rx="10" ry="7" fill="#FFFFFF" stroke="#050505" />
                <rect x="200" y="153" width="6" height="14" fill="#FFFFFF" stroke="#050505" />
                <text x="188" y="182">WC</text>
                {/* Bath */}
                <rect x="215" y="150" width="34" height="22" rx="4" fill="#FFFFFF" stroke="#050505" />
                <circle cx="219" cy="154" r="1.5" fill="#050505" />
                <text x="220" y="182">BATH</text>
                {/* Basin */}
                <rect x="215" y="200" width="20" height="14" rx="3" fill="#FFFFFF" stroke="#050505" />
                <text x="216" y="224">BSN</text>
                {/* Kitchen sink (double bowl) */}
                <rect x="285" y="165" width="30" height="18" fill="#FFFFFF" stroke="#050505" />
                <line x1="300" y1="165" x2="300" y2="183" stroke="#050505" />
                <text x="288" y="196">SINK</text>
                {/* Dishwasher */}
                <rect x="330" y="165" width="18" height="18" fill="#FFFFFF" stroke="#050505" />
                <text x="331" y="196">DW</text>
                {/* Laundry tub + WM + HWU */}
                <rect x="380" y="150" width="22" height="18" fill="#FFFFFF" stroke="#050505" />
                <text x="381" y="180">TUB</text>
                <rect x="405" y="150" width="18" height="18" fill="#FFFFFF" stroke="#050505" />
                <text x="407" y="180">WM</text>
                <circle cx="430" cy="160" r="9" fill="#FFFFFF" stroke="#050505" />
                <text x="425" y="180">HWU</text>
              </g>

              {/* Pins — utility markers with tear-drop shape */}
              {[
                { x: 120, y: 220, c: "#2563EB" }, // water meter
                { x: 230, y: 240, c: "#22C55E" }, // sewer IO
                { x: 150, y: 260, c: "#EAB308" }, // gas meter
                { x: 470, y: 140, c: "#F59E0B" }, // main board
                { x: 470, y: 200, c: "#9333EA" }, // NBN pit
                { x: 90,  y: 330, c: "#DC2626" }, // fire hydrant
                { x: 470, y: 360, c: "#14B8A6" }, // irrigation solenoid
                { x: 260, y: 385, c: "#64748B" }, // storm pit
                { x: 400, y: 310, c: "#22C55E" }, // grease trap
              ].map((p, i) => (
                <g key={i}>
                  <path d={`M${p.x} ${p.y} m-9,-9 a9,9 0 1,1 18,0 a9,9 0 1,1 -18,0 l9,14 z`} fill={p.c} stroke="#050505" strokeWidth="1.5" />
                  <circle cx={p.x} cy={p.y - 9} r="3" fill="#FFFFFF" stroke="#050505" strokeWidth="0.8" />
                </g>
              ))}

              {/* Meter box + stop cock symbols */}
              <rect x="106" y="215" width="10" height="10" fill="#FFFFFF" stroke="#050505" strokeWidth="1" />
              <circle cx="145" cy="245" r="4" fill="#FFFFFF" stroke="#050505" strokeWidth="1" />

              {/* Callout — water meter */}
              <g fontFamily="'JetBrains Mono', monospace" fontSize="8" fill="#050505">
                <line x1="120" y1="220" x2="70" y2="120" stroke="#050505" strokeWidth="0.8" markerEnd="url(#fpArrow)" />
                <rect x="18" y="102" width="106" height="26" fill="#FFFFFF" stroke="#050505" strokeWidth="1.2" />
                <text x="24" y="112" fontWeight="700" letterSpacing="1.3">WATER METER 20 CU</text>
                <text x="24" y="123" fill="#2563EB" fontWeight="700">@ 400 MM · ISO VALVE</text>
              </g>

              {/* Callout — sewer */}
              <g fontFamily="'JetBrains Mono', monospace" fontSize="8" fill="#050505">
                <line x1="380" y1="370" x2="480" y2="420" stroke="#050505" strokeWidth="0.8" markerEnd="url(#fpArrow)" />
                <rect x="470" y="416" width="140" height="30" fill="#FFFFFF" stroke="#050505" strokeWidth="1.2" />
                <text x="476" y="427" fontWeight="700" letterSpacing="1.3">SEWER RUN 100 DWV</text>
                <text x="476" y="438" fill="#22C55E" fontWeight="700">FALL 1:60 · TO COUNCIL MAIN</text>
              </g>

              {/* Callout — grease trap */}
              <g fontFamily="'JetBrains Mono', monospace" fontSize="7" fill="#050505">
                <line x1="400" y1="310" x2="490" y2="280" stroke="#050505" strokeWidth="0.8" markerEnd="url(#fpArrow)" />
                <rect x="484" y="265" width="86" height="20" fill="#FFFFFF" stroke="#050505" strokeWidth="1" />
                <text x="490" y="276" fontWeight="700" letterSpacing="1.2">GREASE TRAP 1000L</text>
              </g>

              {/* Callout — HWU */}
              <g fontFamily="'JetBrains Mono', monospace" fontSize="7" fill="#050505">
                <line x1="430" y1="160" x2="500" y2="100" stroke="#050505" strokeWidth="0.8" markerEnd="url(#fpArrow)" />
                <rect x="496" y="90" width="92" height="18" fill="#FFFFFF" stroke="#050505" strokeWidth="1" />
                <text x="502" y="103" fontWeight="700" letterSpacing="1.2">HWU · 170L GAS INST</text>
              </g>

              {/* Dimensions */}
              <g fontFamily="'JetBrains Mono', monospace" fontSize="7" fill="#050505" opacity="0.7">
                <line x1="150" y1="128" x2="470" y2="128" stroke="#050505" strokeWidth="0.7" markerStart="url(#fpArrow)" markerEnd="url(#fpArrow)" />
                <text x="290" y="124" textAnchor="middle">22.5 M</text>
                <line x1="140" y1="140" x2="140" y2="310" stroke="#050505" strokeWidth="0.7" markerStart="url(#fpArrow)" markerEnd="url(#fpArrow)" />
                <text x="118" y="230" transform="rotate(-90 118 230)">14.0 M</text>
              </g>

              {/* Legend */}
              <g fontFamily="'Barlow Condensed', sans-serif" fontSize="9" fontWeight="700" letterSpacing="1.5">
                <rect x="14" y="330" width="240" height="70" fill="#FFFFFF" stroke="#050505" strokeWidth="1.2" />
                <text x="22" y="345" letterSpacing="2">LEGEND</text>
                {[
                  { c: "#2563EB", l: "WATER" },
                  { c: "#DC2626", l: "FIRE / HW" },
                  { c: "#EAB308", l: "GAS" },
                  { c: "#22C55E", l: "SEWER" },
                  { c: "#64748B", l: "STORM" },
                  { c: "#F59E0B", l: "ELEC" },
                  { c: "#9333EA", l: "NBN" },
                  { c: "#F97316", l: "TELS" },
                  { c: "#14B8A6", l: "IRRIG" },
                ].map((it, i) => (
                  <g key={it.l} transform={`translate(${22 + (i % 3) * 76}, ${360 + Math.floor(i / 3) * 14})`}>
                    <rect width="12" height="8" fill={it.c} stroke="#050505" strokeWidth="0.6" />
                    <text x="16" y="8" fill="#050505">{it.l}</text>
                  </g>
                ))}
              </g>

              {/* GPS / Address */}
              <g fontFamily="'JetBrains Mono', monospace" fontSize="8" fill="#050505">
                <rect x="450" y="52" width="140" height="36" fill="#FFFFFF" stroke="#050505" strokeWidth="1.2" />
                <text x="458" y="63" fontWeight="700" letterSpacing="1.3">LAT -33.86880</text>
                <text x="458" y="74" fontWeight="700" letterSpacing="1.3">LNG 151.20930</text>
                <text x="458" y="85" fontWeight="700" letterSpacing="1.3">GPS ± 3.2 M</text>
              </g>

              {/* Prepared by chip */}
              <g fontFamily="'Barlow Condensed', sans-serif" fontSize="8" fontWeight="700" letterSpacing="1.4">
                <rect x="278" y="330" width="188" height="28" fill="#050505" />
                <text x="286" y="342" fill="#FAFAFA">PREPARED BY  ·  BRAESIDE P&D</text>
                <text x="286" y="352" fill="#FAFAFA" opacity="0.75">FIELD PLAN · UTILITY LOCATE REV B</text>
              </g>
            </svg>

            <div className="absolute top-3 left-3 flex flex-col gap-2 pointer-events-none">
              <span className="bg-background border-2 border-foreground px-2 py-1 font-mono text-[10px] uppercase tracking-widest">LAT -33.8688</span>
              <span className="bg-background border-2 border-foreground px-2 py-1 font-mono text-[10px] uppercase tracking-widest">LNG 151.2093</span>
            </div>
            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-3 pointer-events-none">
              <div className="bg-background border-2 border-foreground px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest">
                Patterson Lakes PS · Layout
              </div>
              <div className="bg-[#2563EB] text-white border-2 border-foreground px-3 py-1.5 font-heading font-black uppercase text-xs tracking-widest">
                Onsite ready
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Utilities strip */}
      <div id="services" className="border-y-2 border-foreground bg-background">
        <Section className="py-10">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">The services you tag</div>
              <h2 className="font-heading font-black uppercase text-3xl md:text-4xl mt-1">Every locate tool on one grid</h2>
            </div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              Familiar to AU tradies · Aligned to industry colours
            </div>
          </div>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {UTILITIES.map(u => (
              <div key={u.key} className="flex items-center gap-3 border-2 border-foreground bg-background px-3 py-3">
                <span className="w-6 h-6 border-2 border-foreground shrink-0" style={{ background: u.color }} />
                <span className="font-heading font-bold uppercase text-sm tracking-wider">{u.label}</span>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* How it works */}
      <div id="how">
        <Section className="py-16 md:py-20">
          <div className="max-w-2xl">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">How it works</div>
            <h2 className="font-heading font-black uppercase text-3xl md:text-5xl mt-1 leading-tight">
              From ute to report in <span className="bg-foreground text-background px-2">four steps</span>.
            </h2>
          </div>
          <div className="mt-10 grid md:grid-cols-4 gap-4 md:gap-5">
            <StepCard n={1} Icon={MapPinned} title="Open a job" body="Give the site a name, tap 'Use my GPS' to centre the satellite exactly where you're standing." />
            <StepCard n={2} Icon={PinIcon} title="Drop &amp; draw" body="Colour-coded pins, polylines and shapes for water, gas, sewer, electrical, NBN and more. Add depth in cm." />
            <StepCard n={3} Icon={FileText} title="Import plans" body="Upload PDF drawings, photos, or Google Earth KML/KMZ files and sketch straight over them." />
            <StepCard n={4} Icon={Sparkles} title="AI report + email" body="One tap turns the whole job into a plain-English site report and emails it to your client." />
          </div>
        </Section>
      </div>

      {/* Pricing preview */}
      <div id="pricing" className="border-t-2 border-foreground bg-background">
        <Section className="py-16 md:py-20">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Simple pricing</div>
              <h2 className="font-heading font-black uppercase text-3xl md:text-5xl mt-1 leading-tight">
                14-day free trial.<br />Then AUD $49/year.
              </h2>
              <p className="mt-3 max-w-xl font-mono text-sm text-muted-foreground">
                One plan, everything included. Try it free for 14 days from signup — no card charged. Cancel anytime.
              </p>
            </div>
            <Link to={user ? "/billing" : "/register"} data-testid="pricing-cta" className="btn-rugged text-sm inline-flex items-center gap-2">
              Start free trial <ArrowRight className="w-4 h-4" strokeWidth={3} />
            </Link>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-6 max-w-3xl">
            <PricePreview
              popular
              name="Field Plan — Annual"
              tagline="Full access, 12 months, one price"
              price="AUD $49"
              cadence="per year · 14-day free trial"
              features={[
                "14-day free trial from signup",
                "Unlimited jobs, pins & sketches",
                "Photos on every pin",
                "AI site reports + PDF/KML export",
                "CAD (DXF) export — opens in AutoCAD/LibreCAD",
                "Cancel anytime",
              ]}
              cta="Start 14-day trial →"
            />
            <div className="border-2 border-foreground bg-[#FFC72C] p-6 flex flex-col justify-between">
              <div>
                <div className="font-mono text-xs uppercase tracking-widest">Why one plan?</div>
                <h3 className="font-heading font-black uppercase text-2xl mt-1 leading-tight">
                  Priced for tradies, not SaaS
                </h3>
                <p className="mt-3 font-mono text-sm leading-relaxed">
                  Less than a carton a year. Every feature — pins, sketches, AI, PDF, photos — included.
                  No seat fees, no tiered tricks, no lock-in beyond your current 12 months.
                </p>
              </div>
              <div className="mt-4 font-mono text-[11px] uppercase tracking-widest">
                Read-only after trial ends → subscribe to keep editing.
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* Get the app (PWA install) */}
      <GetTheApp />

      {/* Need a Locator? — cross-sell for Braeside Asset Locating */}
      <div id="locator" className="border-t-2 border-foreground bg-[#050505] text-background">
        <Section className="py-16 md:py-20 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 border-2 border-background px-3 py-1 font-mono text-[11px] uppercase tracking-[0.2em] mb-6">
              <span className="w-2 h-2 bg-[#2563EB] inline-block" /> Onsite service · Victoria wide
            </div>
            <h2 className="font-heading font-black uppercase tracking-tighter text-4xl md:text-6xl leading-[0.95]">
              Need a<br />
              <span className="bg-[#2563EB] text-white px-2 inline-block">locator?</span>
            </h2>
            <p className="mt-5 max-w-xl text-background/75 leading-relaxed">
              Field Plan is the tool. When you need boots-on-the-ground service locating —
              GPR, electromagnetic locating, sewer camera runs and onsite mark-ups delivered
              back as a Field Plan report — book <strong className="text-background">Braeside Asset Locating</strong>.
            </p>

            <ul className="mt-6 grid grid-cols-2 gap-x-6 gap-y-2 text-sm text-background/80 max-w-xl">
              <li className="flex items-center gap-2"><Check className="w-4 h-4 shrink-0 text-[#2563EB]" strokeWidth={3} /> Underground service locating</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 shrink-0 text-[#2563EB]" strokeWidth={3} /> Ground Penetrating Radar</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 shrink-0 text-[#2563EB]" strokeWidth={3} /> Sewer &amp; drainage inspection</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 shrink-0 text-[#2563EB]" strokeWidth={3} /> Onsite mark-up &amp; report</li>
            </ul>

            <div className="mt-8 flex flex-wrap gap-3">
              <a href="mailto:travis@braesideplumbing.com.au?subject=Braeside%20Asset%20Locating%20enquiry"
                data-testid="locator-email-btn"
                className="text-base inline-flex items-center gap-2 border-2 border-background bg-[#2563EB] text-white px-5 py-3 font-heading font-black uppercase tracking-widest hover:bg-white hover:text-foreground transition-colors">
                <Mail className="w-4 h-4" /> Email Travis
              </a>
              <a href="mailto:travis@braesideplumbing.com.au"
                data-testid="locator-email-address"
                className="inline-flex items-center border-2 border-background/50 bg-transparent text-background px-4 py-3 font-mono text-xs lowercase tracking-widest hover:border-background transition-colors">
                travis@braesideplumbing.com.au
              </a>
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="relative border-2 border-background bg-[#050505] p-7">
              <div className="absolute -top-3 left-4 bg-[#2563EB] text-white border-2 border-background px-3 py-1 font-heading font-black uppercase text-[11px] tracking-widest">
                Braeside Asset Locating
              </div>
              <div className="grid grid-cols-3 gap-3 mt-4">
                {[
                  { c: "#2563EB", l: "WATER" },
                  { c: "#C8A2C8", l: "HARVEST" },
                  { c: "#DC2626", l: "FIRE" },
                  { c: "#EAB308", l: "GAS" },
                  { c: "#22C55E", l: "SEWER" },
                  { c: "#64748B", l: "STORM" },
                  { c: "#F59E0B", l: "ELEC" },
                  { c: "#9333EA", l: "NBN" },
                  { c: "#F97316", l: "TELS" },
                  { c: "#14B8A6", l: "IRRIG" },
                ].map((s) => (
                  <div key={s.l} className="border-2 border-background/40 p-3 text-center">
                    <div className="w-8 h-8 mx-auto border-2 border-background" style={{ background: s.c }} />
                    <div className="mt-2 font-heading font-black uppercase text-[11px] tracking-widest text-background/90">{s.l}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 border-t-2 border-background/30 pt-4 font-mono text-[11px] uppercase tracking-widest text-background/70 leading-relaxed">
                We locate. You dig with confidence. Every job comes back as a Field Plan report with pins, depths and colour-coded runs — client-ready.
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* Support */}
      <div id="support" className="border-t-2 border-foreground bg-background">
        <Section className="py-16 md:py-20 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Support</div>
            <h2 className="font-heading font-black uppercase text-3xl md:text-5xl mt-1 leading-tight">
              We answer.<br />
              <span className="bg-[#2563EB] text-white px-2">Onsite quick.</span>
            </h2>
            <p className="mt-5 text-muted-foreground">
              Trades tickets get triaged the same day. Emails go straight to a real plumber, not a bot.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="mailto:support@utilitymap.org" data-testid="support-email-btn"
                className="btn-rugged text-sm inline-flex items-center gap-2">
                <Mail className="w-4 h-4" /> Email support
              </a>
            </div>
          </div>
          <div className="md:col-span-7 space-y-3">
            {[
              { q: "Do I need a Google Maps or NearMap account?",
                a: "No — Field Plan ships with free Esri satellite, streets and topo layers plus Google Satellite / Hybrid / Streets and OpenStreetMap. NearMap and Metromap subscribers can paste their own keyed XYZ URL under 'Custom XYZ URL' in the map picker." },
              { q: "Can I use it offline?",
                a: "The app shell is cached so the icon opens instantly, but the map tiles, AI reports and photos need internet. Any edits you make while offline are auto-saved to the server as soon as you're back on 4G/5G/Wi-Fi." },
              { q: "How do I cancel?",
                a: "Head to Billing → Manage billing. Stripe/PayPal will keep the account active until the end of the current billing period, then downgrade automatically." },
              { q: "Can I export my job data?",
                a: "Yes — every job can be exported as a PDF (client-ready site report) or a KML file that opens directly in Google Earth, Google My Maps, or any GIS tool." },
              { q: "Something's broken. What now?",
                a: "Email support@utilitymap.org with the job name, a screenshot and your phone model. A tradie-turned-dev responds within one Aussie business day." },
            ].map((it, i) => (
              <details key={i} data-testid={`faq-${i}`}
                className="border-2 border-foreground bg-background p-4 group transition-shadow open:shadow-block-sm">
                <summary className="cursor-pointer font-heading font-black uppercase tracking-tight text-lg flex items-start justify-between gap-4 marker:hidden list-none">
                  <span>{it.q}</span>
                  <span className="font-mono text-xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{it.a}</p>
              </details>
            ))}
          </div>
        </Section>
      </div>

      {/* CTA */}
      <div className="border-t-2 border-foreground">
        <Section className="py-16 md:py-20 text-center">
          <h2 className="font-heading font-black uppercase text-4xl md:text-6xl leading-none">
            Ready to <span className="bg-[#2563EB] text-white px-2">plan onsite</span>?
          </h2>
          <p className="mt-5 text-muted-foreground max-w-xl mx-auto">
            14-day free trial from signup — no card charged. Then AUD $49/year for the lot. Cancel anytime.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to={user ? "/jobs" : "/register"} data-testid="footer-cta-primary" className="btn-rugged text-base inline-flex items-center gap-2">
              {user ? "Open Jobs" : "Start 14-day free trial"} <ArrowRight className="w-4 h-4" strokeWidth={3} />
            </Link>
          </div>
        </Section>
      </div>

      {/* Footer */}
      <footer className="border-t-2 border-foreground bg-background">
        <Section className="py-10 flex flex-wrap items-start justify-between gap-8">
          <div className="max-w-sm">
            <div className="flex items-center gap-3">
              <img src={BRAESIDE_LOGO_URL} alt="Braeside" className="h-10 w-auto" />
              <div className="leading-tight">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">A product of</div>
                <div className="font-heading font-black uppercase text-base">Braeside Plumbing &amp; Drainage</div>
              </div>
            </div>
            <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
              Field Plan is an onsite utility locating &amp; planning tool built by Australian
              tradesmen for Australian tradesmen.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-x-14 gap-y-2 text-xs font-heading font-bold uppercase tracking-widest">
            <a href="#how" className="hover:underline underline-offset-4">How it works</a>
            <a href="#services" className="hover:underline underline-offset-4">Services</a>
            <a href="#download" className="hover:underline underline-offset-4">Download</a>
            <a href="#locator" className="hover:underline underline-offset-4">Need a locator?</a>
            <a href="#support" className="hover:underline underline-offset-4">Support</a>
            <Link to="/login" className="hover:underline underline-offset-4">Log in</Link>
            <Link to="/register" className="hover:underline underline-offset-4">Register</Link>
            <Link to={user ? "/billing" : "/register"} className="hover:underline underline-offset-4">Billing</Link>
          </div>
        </Section>
        <div className="border-t-2 border-foreground">
          <Section className="py-4 flex flex-wrap items-center justify-between gap-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <span>© {new Date().getFullYear()} Braeside Plumbing &amp; Drainage · Field Plan</span>
            <span>Built to survive the ute</span>
          </Section>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
